// Red de AMIGOS: un "peer" estable por jugador (código de amigo) que escucha
// invitaciones directas mientras estás en el juego, y permite invitar a un amigo.
// 100% P2P (broker público), sin backend.
import Peer from 'peerjs';
import { EVENTS } from '../utils/constants.js';
import { getPeerOptions } from './peer-config.js';

const PREFIX = 'palabriks-friend-';
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function randomFriendCode(len = 8) {
  let s = '';
  for (let i = 0; i < len; i++) s += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  return s;
}

export class FriendNet {
  constructor(bus) {
    this.bus = bus;
    this.peer = null;
    this.code = null;
    this.me = null; // { name, avatar, code }
    this._onUnavailable = null;
  }

  /** Registra tu peer estable y escucha invitaciones. me = { name, avatar, code }. */
  async start(code, me) {
    this.code = code;
    this.me = me;
    const opts = await getPeerOptions();
    return new Promise((resolve) => {
      let settled = false;
      // debug:0 -> los pings a amigos desconectados fallan de forma normal; sin ruido en consola.
      const peer = new Peer(PREFIX + code, { ...opts, debug: 0 });
      peer.on('open', () => { settled = true; this.peer = peer; this._bind(); resolve(true); });
      peer.on('error', (e) => {
        if (!settled && e.type === 'unavailable-id') { resolve(false); return; } // ya abierto en otra pestaña
        if (e.type === 'peer-unavailable') return; // amigo desconectado: normal, se resuelve por timeout
        this.bus.emit(EVENTS.NET_ERROR, { error: e?.type || String(e) });
      });
      peer.on('disconnected', () => { try { peer.reconnect(); } catch { /* noop */ } });
    });
  }

  _bind() {
    this.peer.on('connection', (conn) => {
      conn.on('data', (msg) => {
        if (msg?.t === 'invite') {
          this.bus.emit(EVENTS.FRIEND_INVITE, { room: msg.room, from: msg.from || {} });
        } else if (msg?.t === 'reject') {
          // El amigo invitado ha rechazado: avisa al anfitrión que espera en la sala.
          this.bus.emit(EVENTS.FRIEND_REJECT, { from: msg.from || {} });
        }
      });
    });
  }

  /** Avisa al amigo que te invitó de que rechazas la partida. Best-effort. */
  reject(friendCode, timeout = 6000) {
    return new Promise((resolve) => {
      if (!this.peer || friendCode === this.code) return resolve(false);
      let done = false;
      let conn = null;
      const finish = (v) => { if (!done) { done = true; clearTimeout(to); resolve(v); } };
      const to = setTimeout(() => finish(false), timeout);
      conn = this.peer.connect(PREFIX + friendCode, { reliable: true });
      conn.on('open', () => {
        conn.send({ t: 'reject', from: this.me });
        setTimeout(() => { try { conn.close(); } catch { /* noop */ } finish(true); }, 400);
      });
      conn.on('error', () => finish(false));
    });
  }

  /** Invita a un amigo a una sala. Resuelve true si estaba conectado (se entregó). */
  invite(friendCode, room, timeout = 8000) {
    return new Promise((resolve) => {
      if (!this.peer || friendCode === this.code) return resolve(false);
      let done = false;
      let conn = null;
      const finish = (v) => { if (!done) { done = true; clearTimeout(to); resolve(v); } };
      const to = setTimeout(() => finish(false), timeout);
      conn = this.peer.connect(PREFIX + friendCode, { reliable: true });
      conn.on('open', () => {
        conn.send({ t: 'invite', room, from: this.me });
        setTimeout(() => { try { conn.close(); } catch { /* noop */ } finish(true); }, 400);
      });
      conn.on('error', () => finish(false));
    });
  }

  /** ¿Está conectado ese amigo? (best-effort; concurrencia segura: solo open/timeout). */
  ping(friendCode, timeout = 4500) {
    return new Promise((resolve) => {
      if (!this.peer || friendCode === this.code) return resolve(false);
      let done = false;
      let conn = null;
      const finish = (v) => { if (!done) { done = true; clearTimeout(to); try { conn && conn.close(); } catch { /* noop */ } resolve(v); } };
      const to = setTimeout(() => finish(false), timeout);
      conn = this.peer.connect(PREFIX + friendCode, { reliable: true });
      conn.on('open', () => finish(true));
      conn.on('error', () => finish(false));
    });
  }

  dispose() {
    try { this.peer?.destroy(); } catch { /* noop */ }
    this.peer = null;
  }
}
